/**
 * 지원 언어 상수 정의
 * 언어 코드, 라벨, 국기 이모지, RTL 지원 여부를 포함
 */

export type LanguageCode = 'ko' | 'en' | 'zh-hans' | 'zh-hant' | 'vi';

export interface LanguageInfo {
  label: string;
  flag: string;
  rtl: boolean;
}

export const SUPPORTED_LANGUAGES: Record<LanguageCode, LanguageInfo> = {
  ko: {
    label: 'Korean',
    flag: '🇰🇷',
    rtl: false,
  },
  en: {
    label: 'English',
    flag: '🇺🇸',
    rtl: false,
  },
  'zh-hans': {
    label: 'Simplified Chinese',
    flag: '🇨🇳',
    rtl: false,
  },
  'zh-hant': {
    label: 'Traditional Chinese',
    flag: '🇹🇼',
    rtl: false,
  },
  vi: {
    label: 'Vietnamese',
    flag: '🇻🇳',
    rtl: false,
  },
} as const;

/**
 * 지원되는 언어 코드 배열
 */
export const LANGUAGE_CODES = Object.keys(SUPPORTED_LANGUAGES) as LanguageCode[];

/**
 * 기본 언어 코드
 */
export const DEFAULT_LANGUAGE: LanguageCode = 'ko';

/**
 * 필수 언어 코드 (반드시 번역이 있어야 하는 언어)
 */
export const REQUIRED_LANGUAGES: LanguageCode[] = ['ko'];

/**
 * 폴백 언어 순서 (번역이 없을 때 사용할 언어 우선순위)
 */
export const FALLBACK_LANGUAGES: LanguageCode[] = ['ko', 'en'];

/**
 * 언어 코드가 지원되는지 확인하는 함수
 */
export const isValidLanguageCode = (code: string): code is LanguageCode => {
  return LANGUAGE_CODES.includes(code as LanguageCode);
};

/**
 * 언어 정보를 가져오는 함수
 */
export const getLanguageInfo = (code: LanguageCode): LanguageInfo => {
  return SUPPORTED_LANGUAGES[code];
};

/**
 * 비영어 언어 목록 (영어가 아닌 언어들)
 */
export const NON_ENGLISH_LANGUAGES: LanguageCode[] = ['ko', 'zh-hans', 'zh-hant', 'vi'];

/**
 * 활성화된 언어 목록을 가져오는 함수
 */
export const getEnabledLanguages = (): LanguageCode[] => {
  // 환경변수나 설정에서 활성화된 언어를 가져올 수 있지만,
  // 현재는 모든 지원 언어를 반환
  return LANGUAGE_CODES;
};

/**
 * 필수 언어 목록을 가져오는 함수
 */
export const getRequiredLanguages = (): LanguageCode[] => {
  return REQUIRED_LANGUAGES;
};

/**
 * 언어 우선순위 (폴백 순서)
 */
export const LANGUAGE_PRIORITY: LanguageCode[] = FALLBACK_LANGUAGES;
